import { AggregateRating, Thing } from 'schema-dts';

import Seo from '~/src/common/components/Seo';
import { IMAGE_TRANSFORM_PRODUCT, TITLE_SUFFIX } from '~/src/common/components/Seo/constants';
import { getSeoImage } from '~/src/common/components/Seo/utils';
import dayjs from '~/src/common/services/Date';
import I18n from '~/src/common/services/I18n';
import { ProductDetailed } from '~/src/common/typings/product';
import constants from '~/src/constants';

interface Props {
  product: ProductDetailed;
}

export const SeoProduct = ({ product }: Props) => {
  const { name, id, slug, images, articleParents, itemPrice, breadcrumbs, seo, rating } = product;

  const { canonical, title, description } = seo;

  // Données de base
  const uid = id;
  const metaTitle = title ?? name + TITLE_SUFFIX;
  const image = getSeoImage(images?.[0]?.url, IMAGE_TRANSFORM_PRODUCT);
  const metaCanonical = canonical ?? `${constants.SITE_URL}/produit/${slug}`;

  // Marque et catégorie parente
  const brand = constants.BRAND_NAME;
  const category = articleParents?.[0]?.name;

  // Génération de la description
  const defaultDescription = I18n.t('view-product-screen.meta-description', { name });
  const metaDescription = description ?? defaultDescription;

  const aggregateRating = rating
    ? ({
        '@type': 'AggregateRating',
        'bestRating': '5',
        'ratingValue': rating.average.toFixed(1),
        'ratingCount': String(rating.nbRatings),
      } as AggregateRating) // Problème de typage pour ratingCount
    : undefined;

  // Prépration du contenu
  const schemas: Thing[] = [
    // https://schema.org/Product
    {
      '@type': 'Product',
      // Toujours présent
      'sku': uid,
      'name': name,
      'productID': uid,
      'description': defaultDescription,
      // Potentiellement non définis
      ...(brand ? { brand } : null),
      ...(image ? { image } : null),
      ...(category ? { category } : null),
      'aggregateRating': aggregateRating,
      // Prix actuel du produit
      'offers': {
        '@type': 'Offer',
        'availability': 'https://schema.org/InStock',
        'url': metaCanonical,
        'price': (itemPrice / 100).toFixed(2),
        'priceCurrency': 'EUR',
        'priceValidUntil': dayjs().add(1, 'day').toISOString(),
      },
    },
    // https://schema.org/BreadcrumbList
    {
      '@type': 'BreadcrumbList',
      'name': 'Categories breadcrumbs',
      'itemListElement': [
        ...(breadcrumbs ?? [])
          .filter(c => c.enabled)
          .map(c => ({
            name: c.name,
            item: `${constants.SITE_URL}/categorie/${c.slug}`,
          })),
        { name },
      ].map((item, index) => ({
        '@type': 'ListItem' as const,
        'position': index + 1,
        ...item,
      })),
    },
  ];

  return (
    <Seo
      title={metaTitle}
      image={image}
      metaDescr={metaDescription}
      canonicalUrl={metaCanonical}
      schemas={schemas}
      noSuffix
    />
  );
};
